import { BriefcaseIcon, UserIcon } from '@heroicons/react/24/outline';
import {
  type ActiveFilter,
  type FilterDefinition,
  type FilterGeneratorContext,
  type FilterSuggestion,
  type SerializedFilter
} from '@oysterjs/ui/search-filter';
import type { ClientFilterInput } from '~/api/graphql';
import {
  BaseDateFilter,
  BaseSearchFilter,
  BaseUserFilter,
  getSinceDate
} from '~/api/data/filter-base';

export type ClientFilter = 'search' | 'line' | 'owner' | 'date';

/** The owner filter's value for "no owner", which no agency user id can collide with. */
export const UNASSIGNED_OWNER = 'unassigned';

const LINE_OPTIONS = [
  { value: 'commercial', label: 'Commercial' },
  { value: 'personal', label: 'Personal' }
] as const;

/** Map active filters to the server-side ClientFilterInput. */
export function activeFiltersToClientFilter(
  filters: ActiveFilter<ClientFilter>[]
): ClientFilterInput | undefined {
  const out: ClientFilterInput = {};
  for (const f of filters) {
    switch (f.type) {
      case 'search':
        out.search = f.value;
        break;
      case 'line':
        // Both lines selected is an OR on the server, which is what picking
        // two values of one filter reads as.
        if (f.value === 'commercial') out.isCommercial = true;
        if (f.value === 'personal') out.isPersonal = true;
        break;
      case 'owner':
        if (f.value === UNASSIGNED_OWNER) {
          out.hasOwner = false;
        } else {
          out.agencyUserOwnerId = f.value;
        }
        break;
      case 'date': {
        const since = getSinceDate(f.value);
        if (since) out.updatedAfter = since;
        break;
      }
    }
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

export class LineFilter implements FilterDefinition<ClientFilter> {
  readonly type = 'line' as const;
  readonly icon = BriefcaseIcon;
  readonly priority = 0;
  readonly isUnique = false;

  generate({ query, activeFilters }: FilterGeneratorContext<ClientFilter>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    const chosen = new Set(activeFilters.filter((f) => f.type === 'line').map((f) => f.value));
    return LINE_OPTIONS.filter(
      (o) => !chosen.has(o.value) && (o.label.toLowerCase().includes(q) || 'line'.includes(q))
    ).map((o) => ({ value: o.value, displayLabel: 'Line is', displayValue: o.label }));
  }

  deserialize(s: SerializedFilter<ClientFilter>): ActiveFilter<ClientFilter> | null {
    const option = LINE_OPTIONS.find((o) => o.value === s.value);
    if (!option) return null;
    return {
      id: `line-${option.value}`,
      type: this.type,
      value: option.value,
      displayLabel: 'Line is',
      displayValue: option.label
    };
  }
}

/**
 * Unique because the server filters on one owner. Wraps BaseUserFilter for its
 * cached user list rather than extending it, since that class fixes isUnique to
 * false and labels its chips "User is".
 */
export class OwnerFilter implements FilterDefinition<ClientFilter> {
  readonly type = 'owner' as const;
  readonly icon = UserIcon;
  readonly priority = 10;
  readonly isUnique = true;
  private readonly users = new BaseUserFilter<ClientFilter>('owner');

  async generate({
    query,
    activeFilters
  }: FilterGeneratorContext<ClientFilter>): Promise<FilterSuggestion[]> {
    const q = query.trim().toLowerCase();
    // Typing the filter's own name lists everyone, as the status and preset
    // filters on Workspaces do.
    const namesFilter = 'owner'.includes(q);
    const users = await this.users.generate({ query: namesFilter ? '' : query, activeFilters });
    const unassigned: FilterSuggestion[] =
      namesFilter || 'unassigned'.includes(q)
        ? [{ value: UNASSIGNED_OWNER, displayLabel: 'Owner is', displayValue: 'Unassigned' }]
        : [];
    return [...unassigned, ...users.map((u) => ({ ...u, displayLabel: 'Owner is' }))];
  }

  deserialize(s: SerializedFilter<ClientFilter>): ActiveFilter<ClientFilter> {
    return {
      id: `owner-${s.value}`,
      type: this.type,
      value: s.value,
      displayLabel: 'Owner is',
      displayValue: s.displayValue
    };
  }
}

/** "Last activity is since last week", matching the column it filters on. */
export class LastActivityFilter extends BaseDateFilter<ClientFilter> {
  constructor() {
    super('date');
  }

  generate({ query, activeFilters }: FilterGeneratorContext<ClientFilter>): FilterSuggestion[] {
    const q = query.trim().toLowerCase();
    return super
      .generate({ query: 'last activity'.includes(q) ? '' : query, activeFilters })
      .map((s) => ({ ...s, displayLabel: 'Last activity' }));
  }

  deserialize(s: SerializedFilter<ClientFilter>): ActiveFilter<ClientFilter> {
    return { ...super.deserialize(s), displayLabel: 'Last activity' };
  }
}

export class SearchFilter extends BaseSearchFilter<ClientFilter> {
  constructor() {
    super('search');
  }
}

export const createClientFilterDefinitions = (): FilterDefinition<ClientFilter>[] => [
  new LineFilter(),
  new OwnerFilter(),
  new LastActivityFilter(),
  new SearchFilter()
];
